import React, { useState, createElement, Component } from 'react';
import { KpiCard } from '../KpiCard';
import { Plus, Search, FileText, Download, Eye, Trash2, X, Paperclip, Calendar, CheckCircle2, AlertCircle, Clock, Upload } from 'lucide-react';
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
type ContractStatus = 'Attivo' | 'In scadenza' | 'Scaduto' | 'Bozza';
type ContractAttachment = {
  name: string;
  size: string;
  kind: 'contract' | 'additional';
};
type Contract = {
  id: string;
  carrier: string;
  reference: string;
  startDate: string;
  endDate: string;
  shipmentsIncluded: number;
  status: ContractStatus;
  attachments: ContractAttachment[];
  notes?: string;
};
const STATUS_CONFIG: Record<ContractStatus, {
  bg: string;
  text: string;
  border: string;
  icon: ComponentType<any>;
}> = {
  Attivo: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: CheckCircle2
  },
  'In scadenza': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: Clock
  },
  Scaduto: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    icon: AlertCircle
  },
  Bozza: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    icon: FileText
  }
};
const INITIAL_CONTRACTS: Contract[] = [{
  id: 'CTR-2026-001',
  carrier: 'FedEx',
  reference: 'Express Italia 2026',
  startDate: '01/01/2026',
  endDate: '31/12/2026',
  shipmentsIncluded: 5000,
  status: 'Attivo',
  attachments: [{
    name: 'Contratto_FedEx_2026.pdf',
    size: '2.4 MB',
    kind: 'contract'
  }, {
    name: 'Allegato_A_tariffe.pdf',
    size: '0.6 MB',
    kind: 'additional'
  }, {
    name: 'Allegato_B_SLA.pdf',
    size: '0.4 MB',
    kind: 'additional'
  }],
  notes: 'Sconto 12% su spedizioni internazionali'
}, {
  id: 'CTR-2025-008',
  carrier: 'GLS',
  reference: 'Standard Nazionale',
  startDate: '15/03/2025',
  endDate: '14/03/2026',
  shipmentsIncluded: 3500,
  status: 'In scadenza',
  attachments: [{
    name: 'Contratto_GLS_2025.pdf',
    size: '1.8 MB',
    kind: 'contract'
  }]
}, {
  id: 'CTR-2026-002',
  carrier: 'UPS',
  reference: 'Express Worldwide',
  startDate: '10/02/2026',
  endDate: '09/02/2027',
  shipmentsIncluded: 4200,
  status: 'Attivo',
  attachments: [{
    name: 'Contratto_UPS_2026.pdf',
    size: '3.1 MB',
    kind: 'contract'
  }, {
    name: 'Addendum_resi.pdf',
    size: '0.3 MB',
    kind: 'additional'
  }],
  notes: 'Include resi gratuiti fino a 200/mese'
}, {
  id: 'CTR-2024-015',
  carrier: 'BRT',
  reference: 'BRT Sicilia & Sud',
  startDate: '01/06/2024',
  endDate: '31/05/2025',
  shipmentsIncluded: 2000,
  status: 'Scaduto',
  attachments: [{
    name: 'Contratto_BRT_2024.pdf',
    size: '1.2 MB',
    kind: 'contract'
  }]
}, {
  id: 'CTR-2026-003',
  carrier: 'SDA',
  reference: 'SDA Standard',
  startDate: '01/03/2026',
  endDate: '28/02/2027',
  shipmentsIncluded: 2800,
  status: 'Bozza',
  attachments: [{
    name: 'Bozza_SDA_2026.pdf',
    size: '0.8 MB',
    kind: 'contract'
  }],
  notes: 'In attesa di firma del referente legale'
}];
export function Contratti() {
  const [contracts, setContracts] = useState<Contract[]>(INITIAL_CONTRACTS);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Tutti' | ContractStatus>('Tutti');
  const [creating, setCreating] = useState(false);
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const downloadAttachment = (c: Contract, attachment: ContractAttachment) => {
    const content = `${attachment.kind === 'contract' ? 'CONTRATTO CORRIERE' : 'ALLEGATO CONTRATTO'}\n\n` + `File: ${attachment.name}\n` + `Riferimento: ${c.reference}\n` + `ID: ${c.id}\n` + `Corriere: ${c.carrier}\n` + `Validità: ${c.startDate} → ${c.endDate}\n` + `Spedizioni incluse/mese: ${c.shipmentsIncluded.toLocaleString('it-IT')}\n` + `Stato: ${c.status}\n` + (c.notes ? `\nNote:\n${c.notes}\n` : '');
    const blob = new Blob([content], {
      type: 'application/pdf'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const handleDownload = (c: Contract) => {
    const main = c.attachments.find(a => a.kind === 'contract') ?? c.attachments[0];
    if (main) downloadAttachment(c, main);
  };
  const confirmDelete = () => {
    if (contractToDelete) {
      removeContract(contractToDelete.id);
      setContractToDelete(null);
    }
  };
  const filtered = contracts.filter(c => {
    const matchQ = !query.trim() || `${c.carrier} ${c.reference} ${c.id}`.toLowerCase().includes(query.toLowerCase());
    const matchS = statusFilter === 'Tutti' || c.status === statusFilter;
    return matchQ && matchS;
  });
  const activeCount = contracts.filter(c => c.status === 'Attivo').length;
  const expiringCount = contracts.filter(c => c.status === 'In scadenza').length;
  const totalShipmentsIncluded = contracts.filter(c => c.status === 'Attivo').reduce((acc, c) => acc + c.shipmentsIncluded, 0);
  const addContract = (c: Contract) => {
    setContracts(prev => [c, ...prev]);
    setCreating(false);
  };
  const removeContract = (id: string) => {
    setContracts(prev => prev.filter(c => c.id !== id));
  };
  return <div className="space-y-6" data-id="element-1548">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-1549">
        <div data-id="element-1550">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1551">
            Contratti
          </h1>
          <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-1552">
            Gestisci i contratti attivi con i corrieri e carica i documenti
            firmati.
          </p>
        </div>
        <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1553">
          <Plus className="h-4 w-4" data-id="element-1554" />
          Nuovo contratto
        </button>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1555">
        <KpiCard label="Contratti attivi" value={String(activeCount)} data-id="element-1556" />
        <KpiCard label="In scadenza" value={String(expiringCount)} data-id="element-1557" />
        <KpiCard label="Spedizioni/mese incluse" value={totalShipmentsIncluded.toLocaleString('it-IT')} data-id="element-1558" />
        <KpiCard label="Corrieri partner" value={String(new Set(contracts.map(c => c.carrier)).size)} data-id="element-1559" />
      </section>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-1560">
        <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap border-b border-[#1F1F1F]" data-id="element-1561">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] overflow-x-auto" data-id="element-1562">
            {(['Tutti', 'Attivo', 'In scadenza', 'Scaduto', 'Bozza'] as const).map(s => <button key={s} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${statusFilter === s ? 'bg-[#1F1F1F] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA]'}`} data-id="element-1563">
                {s}
              </button>)}
          </div>
          <div className="relative ml-auto w-full sm:w-auto" data-id="element-1564">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1565" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca per corriere o riferimento..." className="w-full sm:w-72 pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1566" />
          </div>
        </div>

        {filtered.length === 0 ? <div className="p-12 text-center" data-id="element-1567">
            <FileText className="h-8 w-8 text-[#6B6B6B] mx-auto mb-3" data-id="element-1568" />
            <p className="text-sm text-[#9A9A9A]" data-id="element-1569">Nessun contratto trovato.</p>
          </div> : <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-1570">
              {filtered.map(c => {
            const cfg = STATUS_CONFIG[c.status];
            const Icon = cfg.icon;
            return <div key={c.id} className="p-4" data-id="element-1571">
                    <div className="flex items-start justify-between gap-3" data-id="element-1572">
                      <div className="flex items-start gap-3 min-w-0" data-id="element-1573">
                        <div className="h-9 w-12 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-1574">
                          <img src={CARRIER_LOGOS[c.carrier]} alt={c.carrier} className="max-w-full max-h-full object-contain" data-id="element-1575" />
                        </div>
                        <div className="min-w-0" data-id="element-1576">
                          <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-1577">
                            {c.reference}
                          </div>
                          <div className="text-[11px] font-mono text-[#6B6B6B] mt-0.5" data-id="element-1578">
                            {c.id}
                          </div>
                          <div className="text-[11px] text-[#9A9A9A] mt-1" data-id="element-1579">
                            {c.startDate} → {c.endDate}
                          </div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border} shrink-0`} data-id="element-1580">
                        <Icon className="h-3 w-3" data-id="element-1581" />
                        {c.status}
                      </span>
                    </div>
                  </div>;
          })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-1582">
              <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-1583">
                <thead data-id="element-1584">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-1585">
                    <th className="px-5 py-3 font-medium" data-id="element-1586">ID</th>
                    <th className="px-5 py-3 font-medium" data-id="element-1587">Corriere</th>
                    <th className="px-5 py-3 font-medium" data-id="element-1588">Riferimento</th>
                    <th className="px-5 py-3 font-medium" data-id="element-1589">Validità</th>
                    <th className="px-5 py-3 font-medium text-right" data-id="element-1590">
                      Sped./mese
                    </th>
                    <th className="px-5 py-3 font-medium" data-id="element-1591">Allegati</th>
                    <th className="px-5 py-3 font-medium" data-id="element-1592">Stato</th>
                    <th className="px-5 py-3 font-medium w-8" data-id="element-1593" />
                  </tr>
                </thead>
                <tbody data-id="element-1594">
                  {filtered.map(c => {
                const cfg = STATUS_CONFIG[c.status];
                const Icon = cfg.icon;
                return <tr key={c.id} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors" data-id="element-1595">
                        <td className="px-5 py-3.5 font-mono text-xs text-[#9A9A9A]" data-id="element-1596">
                          {c.id}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-1597">
                          <div className="flex items-center gap-2.5" data-id="element-1598">
                            <div className="h-7 w-9 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-1599">
                              <img src={CARRIER_LOGOS[c.carrier]} alt={c.carrier} className="max-w-full max-h-full object-contain" data-id="element-1600" />
                            </div>
                            <span className="text-sm text-[#FAFAFA]" data-id="element-1601">
                              {c.carrier}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-[#FAFAFA] max-w-[240px] truncate" data-id="element-1602">
                          {c.reference}
                        </td>
                        <td className="px-5 py-3.5 text-xs font-mono text-[#9A9A9A]" data-id="element-1603">
                          {c.startDate} → {c.endDate}
                        </td>
                        <td className="px-5 py-3.5 text-right font-mono text-[#FAFAFA]" data-id="element-1604">
                          {c.shipmentsIncluded.toLocaleString('it-IT')}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-1605">
                          <button type="button" onClick={() => setPreviewContract(c)} className="inline-flex items-center gap-1.5 text-xs text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-1606">
                            <Paperclip className="h-3.5 w-3.5" data-id="element-1607" />
                            {c.attachments.length} file
                          </button>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-1608">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-1609">
                            <Icon className="h-3 w-3" data-id="element-1610" />
                            {c.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right" data-id="element-1611">
                          <div className="flex items-center gap-1 justify-end" data-id="element-1612">
                            <button type="button" onClick={() => setPreviewContract(c)} className="p-1.5 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#1F1F1F] transition" aria-label="Visualizza" data-id="element-1613">
                              <Eye className="h-3.5 w-3.5" data-id="element-1614" />
                            </button>
                            <button type="button" onClick={() => handleDownload(c)} className="p-1.5 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#1F1F1F] transition" aria-label="Scarica" data-id="element-1615">
                              <Download className="h-3.5 w-3.5" data-id="element-1616" />
                            </button>
                            <button type="button" onClick={() => setContractToDelete(c)} className="p-1.5 rounded text-[#9A9A9A] hover:text-[#EF4444] hover:bg-[#1F1F1F] transition" aria-label="Elimina" data-id="element-1617">
                              <Trash2 className="h-3.5 w-3.5" data-id="element-1618" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </>}
      </div>

      {creating && <NewContractDrawer onClose={() => setCreating(false)} onSubmit={addContract} data-id="element-1619" />}

      {previewContract && <ContractPreviewModal contract={previewContract} onClose={() => setPreviewContract(null)} onDownloadAttachment={att => downloadAttachment(previewContract, att)} data-id="element-1620" />}

      {contractToDelete && <DeleteConfirmModal contract={contractToDelete} onCancel={() => setContractToDelete(null)} onConfirm={confirmDelete} data-id="element-1621" />}
    </div>;
}
function ContractPreviewModal({
  contract,
  onClose,
  onDownloadAttachment
}: {
  contract: Contract;
  onClose: () => void;
  onDownloadAttachment: (attachment: ContractAttachment) => void;
}) {
  const cfg = STATUS_CONFIG[contract.status];
  const Icon = cfg.icon;
  const mainContract = contract.attachments.find(a => a.kind === 'contract');
  const additional = contract.attachments.filter(a => a.kind === 'additional');
  return <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-1622">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-1623" />
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1624">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#262626]" data-id="element-1625">
          <div className="flex items-center gap-3 min-w-0" data-id="element-1626">
            <div className="h-10 w-12 bg-white rounded p-1 flex items-center justify-center shrink-0" data-id="element-1627">
              <img src={CARRIER_LOGOS[contract.carrier]} alt={contract.carrier} className="max-w-full max-h-full object-contain" data-id="element-1628" />
            </div>
            <div className="min-w-0" data-id="element-1629">
              <h3 className="text-base font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-1630">
                {contract.reference}
              </h3>
              <div className="text-xs font-mono text-[#9A9A9A] mt-0.5" data-id="element-1631">
                {contract.id} · {contract.carrier}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition shrink-0" data-id="element-1632">
            <X className="h-4 w-4" data-id="element-1633" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5" data-id="element-1634">
          <div className="flex items-center gap-2" data-id="element-1635">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-1636">
              <Icon className="h-3 w-3" data-id="element-1637" />
              {contract.status}
            </span>
            <span className="text-xs text-[#9A9A9A]" data-id="element-1638">
              Validità {contract.startDate} → {contract.endDate}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1639">
            <div data-id="element-1640">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1641">
                Sped. incluse/mese
              </div>
              <div className="text-sm font-mono text-[#FAFAFA]" data-id="element-1642">
                {contract.shipmentsIncluded.toLocaleString('it-IT')}
              </div>
            </div>
            <div data-id="element-1643">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1" data-id="element-1644">
                Durata
              </div>
              <div className="text-sm font-mono text-[#FAFAFA]" data-id="element-1645">
                {contract.startDate} → {contract.endDate}
              </div>
            </div>
          </div>

          {contract.notes && <div data-id="element-1646">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-2" data-id="element-1647">
                Note interne
              </div>
              <p className="text-sm text-[#FAFAFA] bg-[#181818] border border-[#262626] rounded-lg p-3 whitespace-pre-wrap" data-id="element-1648">
                {contract.notes}
              </p>
            </div>}

          {mainContract && <div data-id="element-1649">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-2" data-id="element-1650">
                Contratto principale
              </div>
              <AttachmentRow attachment={mainContract} onDownload={() => onDownloadAttachment(mainContract)} accent data-id="element-1651" />
            </div>}

          {additional.length > 0 && <div data-id="element-1652">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-2" data-id="element-1653">
                Allegati aggiuntivi ({additional.length})
              </div>
              <div className="space-y-2" data-id="element-1654">
                {additional.map((att, i) => <AttachmentRow key={i} attachment={att} onDownload={() => onDownloadAttachment(att)} data-id="element-1655" />)}
              </div>
            </div>}
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2" data-id="element-1656">
          <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-1657">
            Chiudi
          </button>
          {mainContract && <button type="button" onClick={() => onDownloadAttachment(mainContract)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-1658">
              <Download className="h-4 w-4" data-id="element-1659" />
              Scarica contratto
            </button>}
        </div>
      </div>
    </div>;
}
function DeleteConfirmModal({
  contract,
  onCancel,
  onConfirm
}: {
  contract: Contract;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-1660">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} data-id="element-1661" />
      <div className="relative w-full max-w-md bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden" data-id="element-1662">
        <div className="p-5" data-id="element-1663">
          <div className="flex items-start gap-3" data-id="element-1664">
            <div className="h-10 w-10 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-center justify-center shrink-0" data-id="element-1665">
              <Trash2 className="h-4 w-4 text-[#EF4444]" data-id="element-1666" />
            </div>
            <div className="min-w-0" data-id="element-1667">
              <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-1668">
                Eliminare il contratto?
              </h3>
              <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-1669">
                Stai per eliminare{' '}
                <span className="text-[#FAFAFA] font-medium" data-id="element-1670">
                  {contract.reference}
                </span>{' '}
                ({contract.id}). Questa azione non può essere annullata.
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2" data-id="element-1671">
          <button type="button" onClick={onCancel} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-1672">
            Annulla
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#dc2626] transition" data-id="element-1673">
            <Trash2 className="h-4 w-4" data-id="element-1674" />
            Elimina contratto
          </button>
        </div>
      </div>
    </div>;
}
function RollupCard({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-4" data-id="element-1675">
      <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1676">
        {label}
      </div>
      <div className={`text-2xl font-mono font-semibold mt-1 ${accent ? 'text-[#F5A524]' : 'text-[#FAFAFA]'}`} data-id="element-1677">
        {value}
      </div>
    </div>;
}
function NewContractDrawer({
  onClose,
  onSubmit
}: {
  onClose: () => void;
  onSubmit: (c: Contract) => void;
}) {
  const [carrier, setCarrier] = useState('FedEx');
  const [reference, setReference] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [shipmentsIncluded, setShipmentsIncluded] = useState('');
  const [notes, setNotes] = useState('');
  const [contractFile, setContractFile] = useState<ContractAttachment | null>(null);
  const [additionalFiles, setAdditionalFiles] = useState<ContractAttachment[]>([]);
  const canSubmit = reference && startDate && endDate && contractFile;
  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload: Contract = {
      id: `CTR-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`,
      carrier,
      reference,
      startDate,
      endDate,
      shipmentsIncluded: parseInt(shipmentsIncluded) || 0,
      status: 'Bozza',
      attachments: [contractFile!, ...additionalFiles],
      notes: notes || undefined
    };
    onSubmit(payload);
  };
  const handleContractUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setContractFile({
        name: f.name,
        size: `${(f.size / 1024 / 1024).toFixed(1)} MB`,
        kind: 'contract'
      });
    }
  };
  const handleAdditionalUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newAtts: ContractAttachment[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      newAtts.push({
        name: f.name,
        size: `${(f.size / 1024 / 1024).toFixed(1)} MB`,
        kind: 'additional'
      });
    }
    setAdditionalFiles(prev => [...prev, ...newAtts]);
    e.target.value = '';
  };
  const removeAdditional = (idx: number) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== idx));
  };
  return <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-id="element-1678">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-1679" />
      <div className="relative w-full sm:max-w-lg bg-[#0A0A0A] border-l border-[#262626] flex flex-col" data-id="element-1680">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#262626]" data-id="element-1681">
          <div data-id="element-1682">
            <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-1683">
              Nuovo contratto
            </h3>
            <p className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-1684">
              Aggiungi un contratto e carica il documento firmato.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" data-id="element-1685">
            <X className="h-4 w-4" data-id="element-1686" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-id="element-1687">
          <FormField label="Corriere" required data-id="element-1688">
            <select value={carrier} onChange={e => setCarrier(e.target.value)} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1689">
              {Object.keys(CARRIER_LOGOS).map(k => <option key={k} data-id="element-1690">{k}</option>)}
            </select>
          </FormField>

          <FormField label="Riferimento contratto" required data-id="element-1691">
            <input type="text" value={reference} onChange={e => setReference(e.target.value)} placeholder="Es. Express Italia 2026" className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1692" />
          </FormField>

          <div className="grid grid-cols-2 gap-3" data-id="element-1693">
            <FormField label="Data inizio" required data-id="element-1694">
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1695" />
            </FormField>
            <FormField label="Data fine" required data-id="element-1696">
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1697" />
            </FormField>
          </div>

          <FormField label="Sped. incluse/mese" data-id="element-1698">
            <input type="number" value={shipmentsIncluded} onChange={e => setShipmentsIncluded(e.target.value)} placeholder="3000" className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] font-mono placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-1699" />
          </FormField>

          <FormField label="Documento contratto (PDF)" required data-id="element-1700">
            <label className={`block w-full p-4 rounded-lg border-2 border-dashed cursor-pointer transition text-center ${contractFile ? 'border-[#D4FF3A]/40 bg-[#D4FF3A]/5' : 'border-[#262626] bg-[#181818] hover:border-[#333]'}`} data-id="element-1701">
              <input type="file" className="sr-only" accept=".pdf,.doc,.docx" onChange={handleContractUpload} data-id="element-1702" />
              {contractFile ? <div className="flex items-center justify-center gap-2 text-sm text-[#D4FF3A]" data-id="element-1703">
                  <Paperclip className="h-4 w-4" data-id="element-1704" />
                  <span className="truncate max-w-[200px]" data-id="element-1705">
                    {contractFile.name}
                  </span>
                  <span className="text-xs text-[#6B6B6B]" data-id="element-1706">
                    {contractFile.size}
                  </span>
                </div> : <>
                  <Upload className="h-5 w-5 text-[#6B6B6B] mx-auto mb-1.5" data-id="element-1707" />
                  <div className="text-xs text-[#9A9A9A]" data-id="element-1708">
                    Clicca per caricare il contratto principale
                  </div>
                  <div className="text-[10px] text-[#6B6B6B] mt-0.5" data-id="element-1709">
                    PDF, DOC fino a 10 MB
                  </div>
                </>}
            </label>
          </FormField>

          <FormField label={`Allegati aggiuntivi${additionalFiles.length > 0 ? ` (${additionalFiles.length})` : ''}`} data-id="element-1710">
            <div className="space-y-2" data-id="element-1711">
              {additionalFiles.map((att, i) => <div key={i} className="flex items-center gap-2 p-2 bg-[#181818] border border-[#262626] rounded-lg" data-id="element-1712">
                  <Paperclip className="h-3.5 w-3.5 text-[#9A9A9A] shrink-0" data-id="element-1713" />
                  <div className="flex-1 min-w-0" data-id="element-1714">
                    <div className="text-xs text-[#FAFAFA] truncate" data-id="element-1715">
                      {att.name}
                    </div>
                    <div className="text-[10px] text-[#6B6B6B]" data-id="element-1716">{att.size}</div>
                  </div>
                  <button type="button" onClick={() => removeAdditional(i)} className="p-1 text-[#6B6B6B] hover:text-[#EF4444] transition shrink-0" data-id="element-1717">
                    <X className="h-3.5 w-3.5" data-id="element-1718" />
                  </button>
                </div>)}
              <label className="block w-full p-3 rounded-lg border border-dashed border-[#262626] bg-[#181818] hover:border-[#333] cursor-pointer transition text-center" data-id="element-1719">
                <input type="file" className="sr-only" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" multiple onChange={handleAdditionalUpload} data-id="element-1720" />
                <div className="flex items-center justify-center gap-1.5 text-xs text-[#9A9A9A]" data-id="element-1721">
                  <Plus className="h-3.5 w-3.5" data-id="element-1722" />
                  Aggiungi allegato (es. tariffe, SLA, addendum)
                </div>
              </label>
            </div>
          </FormField>

          <FormField label="Note interne" data-id="element-1723">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Es. clausole particolari, sconti negoziati..." className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40 resize-none" data-id="element-1724" />
          </FormField>
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2" data-id="element-1725">
          <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-1726">
            Annulla
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition ${canSubmit ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#1F1F1F] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-1727">
            Salva contratto
          </button>
        </div>
      </div>
    </div>;
}
function FormField({
  label,
  required,
  children
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div data-id="element-1728">
      <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-1729">
        {label}
        {required && <span className="text-[#D4FF3A] ml-0.5" data-id="element-1730">*</span>}
      </label>
      {children}
    </div>;
}
function AttachmentRow({
  attachment,
  onDownload,
  accent
}: {
  attachment: ContractAttachment;
  onDownload: () => void;
  accent?: boolean;
}) {
  return <div className="flex items-center gap-3 p-3 bg-[#181818] border border-[#262626] rounded-lg" data-id="element-1731">
      <div className={`h-9 w-9 rounded flex items-center justify-center shrink-0 ${accent ? 'bg-[#D4FF3A]/10 border border-[#D4FF3A]/30' : 'bg-[#0A0A0A] border border-[#262626]'}`} data-id="element-1732">
        <FileText className={`h-4 w-4 ${accent ? 'text-[#D4FF3A]' : 'text-[#9A9A9A]'}`} data-id="element-1733" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-1734">
        <div className="text-sm text-[#FAFAFA] truncate" data-id="element-1735">{attachment.name}</div>
        <div className="text-xs text-[#6B6B6B] mt-0.5" data-id="element-1736">{attachment.size}</div>
      </div>
      <button type="button" onClick={onDownload} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-[#FAFAFA] text-xs font-medium hover:border-[#333] transition shrink-0" data-id="element-1737">
        <Download className="h-3.5 w-3.5" data-id="element-1738" />
        Scarica
      </button>
    </div>;
}